// src/padplanner/planLifecycle.js
//
// The review screens' rules (S6a2), as pure functions over S6a1's answers, so
// each can be tested and mutation-checked apart from the pages. The server
// decides every transition and refuses what it will not do; these decide
// only what is offered, and say why something is not.
//
//   stepperState    Draft / Review / Approved / Issued (As-built is cut)
//   shownStatus     the status the stepper shows for the version on screen
//   submitState     whether Submit is offered, enabled, and what it says
//   decisionBoxFor  the step the viewer may decide here, or null
//   chainRows       a revision's approval chain, with who it waits for
//   versionRows     the versions strip
//   headActions     the plan page head's buttons, for who is looking and at what
import { VERDICT_HEADLINES, verdictOf } from "./checksVocabulary";
import { STATUS_LABELS } from "./planVocabulary";

export const STEPS = Object.freeze([
  { key: "draft", label: "Draft" },
  { key: "review", label: "Review" },
  { key: "approved", label: "Approved" },
  { key: "issued", label: "Issued" },
]);

// Where each status stands on the stepper: the step it is at, and whether
// that step is waiting on the author (changes requested) rather than moving.
const AT = Object.freeze({
  draft: { at: 0 }, in_review: { at: 1 }, changes_requested: { at: 1, attention: true },
  approved: { at: 2 }, issued: { at: 3, finished: true },
});

/** [{key, label, state: 'done'|'current'|'attention'|'todo'}] for a plan status. */
export function stepperState(status) {
  const s = AT[status] ?? AT.draft;
  return STEPS.map((step, i) => ({
    ...step,
    state: i < s.at || (i === s.at && s.finished) ? "done"
      : i === s.at ? (s.attention ? "attention" : "current") : "todo",
  }));
}

/**
 * The status of the version shown, for the stepper: the plan's own for the
 * current version; for an older one, where it ended (issued, approved,
 * changes requested; submitted and undecided is in review; else a draft).
 */
export function shownStatus({ plan, approval, revNo }) {
  if (revNo === plan.current_revision) return plan.status;
  const r = approval?.revisions?.find((x) => x.rev_no === revNo);
  if (!r) return plan.status;
  if (r.issued_at) return "issued";
  if (r.decided_status === "approved") return "approved";
  if (r.decided_status === "changes_requested") return "changes_requested";
  return r.frozen ? "in_review" : "draft";
}

/** What each step's tooltip says it is. */
export const STEP_STATE_WORDS = Object.freeze({ done: "done", current: "current", attention: "changes requested", todo: "upcoming" });

export const NOT_CHECKED_SUBMIT_TEXT = "Layout not checked — no clearance rules published";
export const NO_ROUTE_TEXT = "No approval route. Choose one, or ask an organization admin to set a default route.";
export const SAVE_FIRST_TEXT = "Save your changes before submitting.";
export const CHECKS_LOADING_TEXT = "Loading checks…";
// The verdict's own words (VERDICT_HEADLINES), then what to do about it.
export const SUBMIT_BLOCKED_TEXT = `${VERDICT_HEADLINES.blocked}: fix the failing checks to submit.`;
export const SUBMIT_STALE_TEXT = `${VERDICT_HEADLINES.stale}: re-run the checks to submit.`;

/**
 * Submit, as offered on the plan page and in the editor:
 * {show, enabled, reason, confirm}. Shown to an editor while the plan is a
 * draft on its current revision. Enabled for a pass, for warnings, and for
 * not_checked (whose confirm says so); disabled for blocked and stale, with
 * the verdict's words, and while there is no route, unsaved work, or no
 * checks yet. `approval` is GET …/approval; `checks` is {loaded, run, rules}.
 */
export function submitState({ approval, checks, canEdit, dirty = false, planNumber = "" }) {
  const no = { show: false, enabled: false, reason: null, confirm: null };
  if (!approval || !canEdit || approval.status !== "draft") return no;
  const rev = approval.current_revision;
  const base = `Submit ${planNumber ? `${planNumber} ` : ""}v${rev} for review? The layout is frozen once it is submitted.`;
  const off = (reason) => ({ show: true, enabled: false, reason, confirm: null });
  // The server says no: for want of a route, say so; otherwise it is not this user's to submit.
  if (!approval.can?.submit) return approval.route ? no : off(NO_ROUTE_TEXT);
  if (dirty) return off(SAVE_FIRST_TEXT);
  if (!checks?.loaded) return off(CHECKS_LOADING_TEXT);
  const verdict = verdictOf(checks);
  if (verdict === "blocked") return off(SUBMIT_BLOCKED_TEXT);
  if (verdict === "stale") return off(SUBMIT_STALE_TEXT);
  if (verdict === "not_checked") return { show: true, enabled: true, reason: NOT_CHECKED_SUBMIT_TEXT, confirm: `${base}\n\n${NOT_CHECKED_SUBMIT_TEXT}.` };
  return { show: true, enabled: true, reason: null, confirm: base };
}

/**
 * The step the viewer may decide on the revision shown, or null. Only on the
 * current revision, only while its step is pending, never for its submitter,
 * and only when the server names that step as the viewer's (can.decide: the
 * next pending step, named to them or to any approver, and they hold
 * can_approve_site_plans).
 */
export function decisionBoxFor({ approval, viewedRevNo, meId }) {
  const d = approval?.can?.decide;
  if (!d || approval.status !== "in_review") return null;
  if (viewedRevNo !== approval.current_revision) return null;
  const rev = approval.revisions?.find((r) => r.rev_no === viewedRevNo);
  if (!rev || !rev.frozen) return null;
  if (!meId || rev.submitted_by?.user_id === meId) return null;
  const pending = rev.steps.find((s) => s.decision === "pending");
  if (!pending || pending.ordinal !== d.ordinal) return null;
  if (pending.approver && pending.approver.user_id !== meId) return null;
  return { ordinal: d.ordinal, role_label: d.role_label };
}

export const NOTE_MIN = 10;
export const NOTE_MAX = 2000;
export const ANY_APPROVER_TEXT = "Any approver";

/**
 * A submitted revision's chain: [{ordinal, role, approver, state, by, at, note}],
 * state one of approved | changes_requested | waiting (the step it is on) |
 * next (after it) | skipped (pending under a decided revision).
 */
export function chainRows(revision) {
  if (!revision?.steps?.length) return [];
  const decided = revision.decided_status === "approved" || revision.decided_status === "changes_requested";
  let waitingSeen = false;
  return revision.steps.map((s) => {
    let state = s.decision;
    if (s.decision === "pending") {
      state = decided ? "skipped" : waitingSeen ? "next" : "waiting";
      waitingSeen = true;
    }
    return {
      ordinal: s.ordinal,
      role: s.role_label,
      approver: s.approver?.display_name ?? (s.approver ? "Unknown user" : ANY_APPROVER_TEXT),
      state,
      by: s.decided_by?.display_name ?? null,
      at: s.decided_at ?? null,
      note: s.note ?? null,
    };
  });
}

export const CHAIN_STATE_WORDS = Object.freeze({
  approved: "approved", changes_requested: "changes requested", waiting: "waiting", next: "next", skipped: "not reached",
});

/** What a revision is, in the versions strip: Draft, Submitted, or its decision. */
export function revisionWord(r) {
  if (r.issued_at) return STATUS_LABELS.issued;
  if (r.decided_status === "approved") return STATUS_LABELS.approved;
  if (r.decided_status === "changes_requested") return STATUS_LABELS.changes_requested;
  return r.frozen ? "Submitted" : STATUS_LABELS.draft;
}

/** The versions strip, oldest first: [{rev_no, word, current, lastApproved, date}]. */
export function versionRows(approval) {
  if (!approval?.revisions) return [];
  return [...approval.revisions].sort((a, b) => a.rev_no - b.rev_no).map((r) => ({
    rev_no: r.rev_no,
    word: revisionWord(r),
    current: r.rev_no === approval.current_revision,
    lastApproved: r.rev_no === approval.last_approved_rev_no,
    date: r.issued_at ?? r.submitted_at ?? null,
  }));
}

/** At most this many versions are buttons in the head; the rest are in the "Older versions" menu. */
export const VERSIONS_SHOWN = 4;

/**
 * The versions the head shows as buttons, and the older ones it folds into a
 * menu: the current, the last approved and the viewed version are always
 * shown, then the newest others up to VERSIONS_SHOWN. Both lists are oldest
 * first, as the strip reads.
 */
export function splitVersions(rows, viewedRevNo, max = VERSIONS_SHOWN) {
  if (rows.length <= max) return { shown: rows, folded: [] };
  const keep = new Set(rows.filter((r) => r.current || r.lastApproved || r.rev_no === viewedRevNo).map((r) => r.rev_no));
  for (const r of [...rows].reverse()) { if (keep.size >= max) break; keep.add(r.rev_no); }
  return { shown: rows.filter((r) => keep.has(r.rev_no)), folded: rows.filter((r) => !keep.has(r.rev_no)) };
}

/**
 * The plan page head's buttons, in order, as keys: open_editor, edit_details,
 * archive, start_revision, download_geojson, download_report, generate_report.
 *
 *   canEdit    GET /plans/:id's can_edit: the edit capability, on a plan not
 *              archived whose current revision is not frozen. Open editor,
 *              Edit details and Archive need exactly that: the server refuses
 *              the details PATCH and the archive on a frozen revision.
 *   approval   GET …/approval: can.start_revision is an editor's on a plan
 *              changes_requested, approved or issued (S6a). Offered on the
 *              current version only, since it copies the current one.
 *   download   for anyone who can see the page: the export is a VIEW read,
 *              gated as the page is, of the version shown, frozen or not.
 *   report     the version shown has a stored plan report (S7c: made at its
 *              Issue). A VIEW read too; a version issued before reports were
 *              stored has none, and is not offered one.
 *   generate   the version shown is issued and has no report, and
 *              can.generate_report (an org_admin who edits; S7d), the plan
 *              archived or not (165). Any issued version, not only the current one.
 */
export function headActions({ canEdit, approval, onCurrent, viewedRevNo = null }) {
  const out = canEdit ? ["open_editor", "edit_details", "archive"] : [];
  if (onCurrent && approval?.can?.start_revision) out.push("start_revision");
  out.push("download_geojson");
  if (reportFor(approval, viewedRevNo)) out.push("download_report");
  else if (approval?.can?.generate_report && approval.revisions?.find((r) => r.rev_no === viewedRevNo)?.issued_at) out.push("generate_report");
  return out;
}

/** The viewed version's stored report, {sha256, byte_size}, or null. */
export function reportFor(approval, revNo) {
  return approval?.revisions?.find((r) => r.rev_no === revNo)?.report ?? null;
}

/** "v3 as issued, PDF, 412 KB": the report button's title. */
export function reportTitle(revNo, report) {
  const kb = Math.max(1, Math.round((report?.byte_size ?? 0) / 1024));
  const when = report?.generated_after_issue_on ? `generated after issue on ${report.generated_after_issue_on}` : "as issued";
  return `v${revNo} ${when}, PDF, ${kb.toLocaleString("en-US")} KB`;
}

/** Generate report's confirmation and its result (S7d). */
export const generateReportConfirm = (revNo) =>
  `Generate the plan report for v${revNo}? It's made from v${revNo} as issued, stamped as generated after issue on today's date, and stored once. It can't be regenerated.`;
export const generatedReportText = (r) =>
  `Report generated for v${r.report_generated.rev_no} on ${r.report_generated.report.generated_after_issue_on}.`;

export const startRevisionConfirm = (current) => `Start revision ${current + 1}? Its layout and wells start as a copy of v${current}.`;
export const startedText = (r) => `Revision ${r.revision_started.rev_no} started: ${r.revision_started.feature_count} features and ${r.revision_started.well_count} wells carried, checks run.`;

// The plan page's head, when the version shown is frozen, and when it is not the current one.
export const FROZEN_TEXT = "This version was submitted, so its layout is read-only. Open editor is offered only on a draft.";
export const olderVersionText = (revNo, current) => `Viewing v${revNo}. The current version is v${current}.`;

/** "Oct 25": a decision's or a version's day, US English. */
export function shortDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
